import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "./Reveal";
import { HERO, IMAGES } from "../data/content";
import { ArrowDown } from "lucide-react";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroBg}
          alt="Cinematic abstract"
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/55 to-[#0a0a0a]/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(10,10,10,0.6)_100%)]" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-end px-6 pb-16 pt-32 lg:px-10 lg:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-6 flex items-center gap-3 text-xs uppercase tracking-luxury text-zinc-300"
        >
          <span className="h-px w-10 bg-gold" />
          {HERO.overline}
        </motion.div>

        <h1 className="font-serif text-[18vw] leading-[0.82] tracking-tighter text-white drop-shadow-[0_4px_20px_rgba(0,0,0,0.75)] sm:text-[15vw] lg:text-[11rem]">
          <MaskedLines lines={[HERO.lines[0]]} delay={0.35} />
          <MaskedLines
            lines={[HERO.lines[1]]}
            delay={0.5}
            lineClassName="italic text-gold"
          />
        </h1>

        <div className="mt-10 grid gap-8 lg:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 lg:col-start-1 text-base leading-relaxed text-zinc-300 max-w-xl"
          >
            {HERO.paragraph}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 lg:col-start-9 text-sm leading-relaxed text-zinc-500 self-end"
          >
            {HERO.tag}
          </motion.p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-2 text-xs uppercase tracking-luxury text-zinc-400 lg:right-10 lg:flex"
      >
        Scroll
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
